import { clsx } from 'clsx'
import { useLightboxState, useController } from 'yet-another-react-lightbox'
import { Flex } from '../Flex'
import {
  ZoomInIcon,
  ZoomOutIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  XIcon,
} from '../icons'
import { UnstyledButton } from '../UnstyledButton'
import { IconButton } from '../IconButton'
import styles from './Lightbox.module.css'

import type { JSX } from 'react'

export const ZoomControls = ({
  zoomLevel,
  maxZoom,
  zoomIn,
  zoomOut,
}: {
  zoomLevel: number
  maxZoom: number
  zoomIn?: () => void
  zoomOut?: () => void
}): JSX.Element | null => {
  const { currentSlide } = useLightboxState()
  // Only show zoom controls for image
  if (currentSlide?.type !== 'image') return null

  return (
    <Flex align="center" justify="center" className={styles.zoomControls}>
      <IconButton
        label="Zoom in"
        onClick={zoomIn}
        variant="tertiary"
        className={styles.zoomButton}
        disabled={zoomLevel >= maxZoom}
      >
        <ZoomInIcon />
      </IconButton>
      <IconButton
        label="Zoom out"
        onClick={zoomOut}
        variant="tertiary"
        className={styles.zoomButton}
        disabled={zoomLevel <= 1}
      >
        <ZoomOutIcon />
      </IconButton>
    </Flex>
  )
}

export const NextButton = (): JSX.Element => {
  const { slides, currentIndex } = useLightboxState()
  const { next } = useController()

  return (
    <UnstyledButton
      aria-label="Next"
      className={clsx(
        styles.navigationButton,
        'yarl__button yarl__navigation_next',
      )}
      disabled={currentIndex + 1 === slides.length}
      onClick={() => next()}
    >
      <Flex className={styles.navigationIcon} align="center" justify="center">
        <ChevronRightIcon color="inverted" size="large" />
      </Flex>
    </UnstyledButton>
  )
}

export const PrevButton = (): JSX.Element => {
  const { currentIndex } = useLightboxState()
  const { prev } = useController()

  return (
    <UnstyledButton
      aria-label="Previous"
      className={clsx(
        styles.navigationButton,
        'yarl__button yarl__navigation_prev',
      )}
      disabled={currentIndex === 0}
      onClick={() => prev()}
    >
      <Flex className={styles.navigationIcon} align="center" justify="center">
        <ChevronLeftIcon color="inverted" size="large" />
      </Flex>
    </UnstyledButton>
  )
}

/**
 * Renders the full close button via yarl's `buttonClose` render slot.
 *
 * We render the button itself (not just an icon via `iconClose`) so the
 * Popcorn `IconButton` is the single interactive element. The `iconClose`
 * slot only swaps the icon inside yarl's own `.yarl__button`, so nesting an
 * `IconButton` there put a `<button>` inside a `<button>` — a `nested-interactive`
 * axe violation plus a `validateDOMNesting` React warning.
 */
export const CloseButton = (): JSX.Element => {
  const { close } = useController()

  return (
    <IconButton
      className={styles.closeButton}
      label="Close Lightbox"
      size="large"
      variant="tertiary"
      onClick={() => close()}
    >
      <XIcon />
    </IconButton>
  )
}
